import config from '../../config';
import store from '../store';
import APIService from './APIService';
import { objectToQueryParams } from '../helpers/common';

export default class ProjectService extends APIService {
  /**
   *
   * @param {Object} params
   * @param {Boolean} asApprover
   * 
   * params example= {
      state = 'current',
      viewAs = 'owner_and_collaborator',
      pageNumber = '1',
      pageSize,
      sortBy = 'projectName',
      sortOrder = 'ASC'
    }
   */
  static loadProjects(
    {
      state = 'current',
      viewAs = 'owner_and_collaborator',
      pageNumber = '1',
      pageSize,
      sortBy = 'projectName',
      sortOrder = 'ASC'
    },
    asApprover
  ) {
    let url = `${config.baseUrlForDiscoveryProject}/projects`;
    if (asApprover) {
      url = `${url}/asApprover`;
    }
    const params = objectToQueryParams({
      pageNumber,
      pageSize,
      sortBy,
      sortOrder,
      state,
      viewAs
    });
    return this.get(`${url}?${params}`);
  }

  /**
   *
   * @param {String} id
   * @param {Object} data
   */
  static setProjectDetails(id, data) {
    return this.put(`${config.baseUrlForDiscoveryProject}/project/${id}`, data);
  }

  /**
   *
   * @param {String} id
   */
  static projectDetails(id) {
    return this.get(`${config.baseUrlForDiscoveryProject}/project/${id}`);
  }
  /**
   *
   * @param {String} id
   */
  static approverComments(id) {
    return this.get(
      `${config.baseUrlForDiscoveryProject}/project/${id}/approverComments`
    );
  }
  /**
   *
   * @param {String} id
   * @param {Object} projectComment
   *    Example -> {
          comment: this.remarks,
          decision: this.selectedAction
        };
   */
  static approverCommentDecision(id, projectComment) {
    return this.post(
      `${config.baseUrlForDiscoveryProject}/project/${id}/decision`,
      projectComment
    );
  }

  /**
   * To create a project
   * @param {Object} projectData
   */
  static createProject(projectData) {
    return this.post(
      `${config.baseUrlForDiscoveryProject}/project`,
      projectData
    );
  }

  /**
   * To get tags of a project
   * @param {String} projectId
   */
  static getTags(projectId) {
    return this.get(`${config.baseUrlForAnnotations}/tags/${projectId}`);
  }

  static getTagsAndComments(projectId) {
    return this.get(
      `${config.baseUrlForAnnotations}/project/${projectId}/latest`
    );
  }
  /**
   * To add Collaborators to the project
   *
   * @param {String} projectId
   * @param {Array<String>} idList
   */
  static saveCollaborators(projectId, idList) {
    let requestObj = {
      collaborators: idList
    };
    let url = config.baseUrlForDiscoveryProject;
    return this.put(`${url}/project/${projectId}/addCollaborators`, requestObj);
  }

  /**
   * To remove the Collaborators from project
   * @param {String} projectId
   * @param {Array<String>} idList
   */
  static removeCollaborators(projectId, idList) {
    let requestObj = {
      collaborators: idList
    };
    let url = config.baseUrlForDiscoveryProject;
    return this.put(
      `${url}/project/${projectId}/removeCollaborators`,
      requestObj
    );
  }

  /**
   *
   * @param {String} projectId
   * @param {Object} urlParams
   * @param {Object} searchBody
   */
  // eslint-disable-next-line
  static searchProject(projectId, urlParams, searchBody = null, cancelToken) {
    urlParams = new URLSearchParams(urlParams);
    const query = urlParams.toString();
    let orgId = store.state.orgId;
    return this.post(
      `${config.baseUrlForProjectSearch}/search/v1/${orgId}/project/${projectId}/archives?${query}`,
      searchBody,
      undefined,
      cancelToken
    );
  }

  /**
   * To get the list of archives for a particular project
   * @param {String} projectId
   * @param {Object} params
   */
  static getArchiveList(projectId, params) {
    params = new URLSearchParams(params);
    const query = params.toString();
    return this.get(
      `${config.baseUrlForDiscoveryProject}/project/${projectId}/archives?${query}`
    );
  }

  /**
   * To get a particular attachment
   * @param {String} selectedArchiveId
   * @param {Number} attachmentIndex
   * @param {String} orgId
   */
  static getAttachment({ selectedArchiveId, attachmentIndex, orgId }) {
    return this.get(
      `${config.baseUrlForProjectSearch}/v1/archives/${encodeURIComponent(
        selectedArchiveId
      )}/viewAttachment/${attachmentIndex}?current_org=${orgId}`,
      'arraybuffer'
    );
  }
  /**
   *
   * @param {Object} params
   * @param {Array<String>} archiveIds
   */
  static getArchiveDetails(params, archiveIds) {
    params['pageNumber'] = 1;
    params = new URLSearchParams(params);
    const query = params.toString();
    return this.post(
      `${config.baseUrlForProjectSearch}/v1/${store.state.orgId}/archiveDetails?${query}`,
      archiveIds
    );
  }
  /**
   *
   * @param {String} projectId
   * @param {Array<String>} archiveIds
   */
  static getTopTags(projectId, archiveIds) {
    return this.post(
      `${config.baseUrlForAnnotations}/tags/${projectId}/top/tags`,
      archiveIds
    );
  }

  /**
   *
   * @param {String} projectId
   * @param {Array<String>} archiveIds
   */
  static addArchives(projectId, archiveIds) {
    return this.put(
      `${config.baseUrlForDiscoveryProject}/project/${projectId}/addArchives`,
      archiveIds
    );
  }

  /**
   *
   * @param {String} projectId
   * @param {Array<String>} archiveIds
   */
  static removeArchives(projectId, archiveIds) {
    return this.put(
      `${config.baseUrlForDiscoveryProject}/project/${projectId}/removeArchives`,
      archiveIds
    );
  }

  /**
   *
   * @param {String} projectId
   */
  static archiveProject(projectId) {
    return this.put(
      `${config.baseUrlForDiscoveryProject}/project/${projectId}/archive`
    );
  }
  /**
   *
   * @param {String} id
   */
  static getActiveDiscoveryProjectsCount() {
    const currentOrg = store.state.currentlySelectedOrgId;
    return this.get(
      `${config.baseUrlForDiscoveryProject}/discovery/project/count/${currentOrg}`
    );
  }
  /**
   * @param {string} projectId
   */
  static getSearchHistory({ projectId }) {
    const currentOrg = store.state.orgId;
    return this.get(
      `${config.baseUrlForDiscoveryProject}/search/history/org/${currentOrg}/project/${projectId}`
    );
  }

  /**
   * @param {string} projectId
   * @param {Object} queryString
   *
   */
  static addSearchHistory({ projectId, queryString }) {
    const currentOrg = store.state.orgId;
    return this.post(`${config.baseUrlForDiscoveryProject}/search/history`, {
      orgId: currentOrg,
      projectId,
      queryString
    });
  }
  /**
   * To export archives of selected project
   *
   * @param {String} projectId
   * @param {String} exportTo, connector Id where we want to export data
   */
  static exportArchives(projectId, exportTo) {
    const userId = store.state.userInformation?.userId;
    const tenantId = store.state.currentOrgData?.tenantId;
    const currentOrg = store.state.orgId;
    const timeZone = store.state.timezone?.source;

    return this.post(
      `${config.baseUrlForOffboardingService}/discovery/export/download`,
      {
        orgId: currentOrg,
        requestedBy: userId,
        projectId,
        tenantId,
        timeZone,
        exportTo
      }
    );
  }
  /**
   * To get export job status
   */
  static getExportProjectStatus() {
    const currentOrg = store.state.orgId;
    return this.get(
      `${config.baseUrlForOffboardingService}/discovery/export/${currentOrg}`
    );
  }
}
